/*
 * @description 音频章节打点时间轴（横向）
 *  - 按 type=chapter 把整条音轨切分成若干章节段，段宽与时长成比例
 *  - type=annotation 作为圆点挂在底部轨道对应时间位置
 *  - 当前播放所在章节段高亮，底部蓝色进度线 + 圆形把手指示播放进度
 *  - hover 章节段顶部弹出该章节名 tooltip
 */
import { memo, useMemo, useCallback } from 'react';
import './chapter-timeline.css';

export type ChapterTimelineItemType = 'chapter' | 'annotation';

export interface ChapterTimelineItem {
  /** 展示文案 */
  text: string;
  /** 时间（秒） */
  time: number;
  /** 类型 */
  type: ChapterTimelineItemType;
}

export interface ChapterTimelineProps {
  /** 扁平数据：章节 + 批注 */
  list: ChapterTimelineItem[];
  /** 音频总时长（秒） */
  duration: number;
  /** 当前播放位置（秒） */
  currentTime: number;
  /** 跳转回调 */
  onSeek?: (seconds: number) => void;
}

interface Segment {
  text: string;
  start: number;
  end: number;
  annotations: ChapterTimelineItem[];
}

const clampPct = (n: number): number => {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
};

/** 把扁平列表切分成章节段，annotation 归到它前面最近的章节 */
const buildSegments = (list: ChapterTimelineItem[], duration: number): Segment[] => {
  const segments: Segment[] = [];
  let current: Segment | null = null;

  list.forEach((item) => {
    if (item.type === 'chapter') {
      current = { text: item.text, start: item.time, end: duration, annotations: [] };
      segments.push(current);
    } else {
      if (!current) {
        current = { text: '', start: 0, end: duration, annotations: [] };
        segments.push(current);
      }
      current.annotations.push(item);
    }
  });

  // 每段的结束时间 = 下一段的开始时间（最后一段到总时长）
  segments.forEach((seg, i) => {
    // eslint-disable-next-line no-param-reassign
    seg.end = i < segments.length - 1 ? segments[i + 1].start : duration;
  });

  return segments;
};

const ChapterTimeline = memo((props: ChapterTimelineProps) => {
  const { list, duration, currentTime, onSeek } = props;

  const segments = useMemo(() => buildSegments(list, duration), [list, duration]);

  const handleSeek = useCallback(
    (seconds: number) => {
      onSeek?.(seconds);
    },
    [onSeek],
  );

  if (!duration || segments.length === 0) return null;

  return (
    <div className="chapter-timeline">
      <div className="chapter-timeline__segments">
        {segments.map((seg, i) => {
          // 绝对定位 + 百分比：边界精确对应进度条上的时间点(start/duration、end/duration)，与进度条对齐。
          // 间隙不放在这里(放 gap 会缩短总宽、破坏对齐)，而是由内层背景块左右内缩制造，间隙中点落在时间点上。
          const leftPct = clampPct((seg.start / duration) * 100);
          const widthPct = clampPct(((seg.end - seg.start) / duration) * 100);
          const active = currentTime >= seg.start && currentTime < seg.end;
          return (
            <div
              key={`${seg.start}-${i}`}
              className="chapter-timeline__slot"
              style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
            >
              <div
                className={`chapter-timeline__segment ${active ? 'chapter-timeline__segment--active' : ''}`.trim()}
                onClick={() => handleSeek(seg.start)}
                role="button"
                tabIndex={0}
              >
                {seg.text && <span className="chapter-timeline__label">{seg.text}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

ChapterTimeline.displayName = 'ChapterTimeline';

export default ChapterTimeline;
